import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type { UserDetailsResponse, ActivityLogResponse, ActivityLogParams } from '@/types/userCourseProgress.types';

class UserService {
  async getUsers(filters: any = {}) {
    try {
      const page = filters.page || 1;
      const limit = filters.pageSize || 10;

      const payload: Record<string, any> = {
        page,
        limit
      };

      if (filters.search) payload.search = filters.search;
      if (filters.status && filters.status !== 'all') payload.status = filters.status;
      if (filters.role && filters.role !== 'all') payload.role_key = filters.role;
      if (filters.group && filters.group !== 'all') payload.group_key = filters.group;
      if (filters.department && filters.department !== 'all') payload.department = filters.department;

      const response = await apiClient.post(API_ENDPOINTS.USERS.LIST, payload);
      
      let userData = [];
      let total = 0;
      
      if (response.data?.data?.users) {
        userData = response.data.data.users;
        total = response.data.data.totalUsers || userData.length;
      } else if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
        // Handle nested structure: { data: { data: [users...], total: X } }
        userData = response.data.data.data;
        total = response.data.data.total || userData.length;
      } else if (Array.isArray(response.data?.data)) {
        userData = response.data.data;
        total = response.data.meta_data?.total || response.data.total || userData.length;
      }
      
      return {
        data: userData,
        total: total,
        page: page,
        pageSize: limit,
        success: response.data?.success || false
      };
    } catch (error) {
      console.error('Error fetching users:', error);
      return { data: [], total: 0, page: 1, pageSize: 10, success: false };
    }
  }

  async getUserById(id: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USERS.GET_BY_ID(id));
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async createUser(userData: any) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.USERS.CREATE, userData);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(id: string, userData: any) {
    try {
      const response = await apiClient.put(API_ENDPOINTS.USERS.UPDATE(id), userData);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(id: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.USERS.DELETE(id));
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async updateUserStatus(id: string, status: string) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.USERS.UPDATE_STATUS(id), { status });
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  async bulkImportUsers(file: File, options: any = {}) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      Object.keys(options).forEach(key => {
        formData.append(key, options[key]);
      });
      const response = await apiClient.post(API_ENDPOINTS.USER_BULK_IMPORT.INITIATE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk importing users:', error);
      throw error;
    }
  }

  async getUserMetrics() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USERS.METRICS);
      return response.data;
    } catch (error) {
      console.error('Error fetching user metrics:', error);
      throw error;
    }
  }
  
  async getGroups(params: any = {}) {
    try {
      // Build request body with all parameters
      const body: any = {};

      if (params.skip !== undefined) body.skip = params.skip || 0;
      if (params.limit !== undefined) body.limit = params.limit || 10;
      if (params.location) body.location = params.location;
      if (params.group_name) body.group_name = params.group_name;
      if (params.group_key) body.group_key = params.group_key;
      if (params.with_users !== undefined) body.with_users = params.with_users;
      if (params.search && !body.group_name) body.group_name = params.search;

      const response = await apiClient.post(API_ENDPOINTS.GROUPS.LIST, body);
      return response.data;
    } catch (error) {
      console.error('Error fetching groups:', error);
      throw error;
    }
  }
  
  // Lightweight, server-side department search for Global Search. Hits the dropdown
  // endpoint (name+key projection, no analytics) with a server-side group_name regex
  // filter + limit — much faster than the full /groups/list analytics endpoint.
  async searchDepartments(search: string, limit = 8) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.SEARCH, {
        params: { search, limit },
      });
      return response.data;
    } catch (error) {
      console.error('Error searching departments:', error);
      throw error;
    }
  }

  async getGroupByKey(key: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.GET_BY_KEY(key));
      return response.data;
    } catch (error) {
      console.error('Error fetching group:', error);
      throw error;
    }
  }

  async getGroupsDropdown() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.DROPDOWN);
      return response.data;
    } catch (error) {
      console.error('Error fetching groups dropdown:', error);
      throw error;
    }
  }

  async getRolesDropdown() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ROLES.DROPDOWN);
      return response.data;
    } catch (error) {
      console.error('Error fetching roles dropdown:', error);
      throw error;
    }
  }
   async getCourseDropdown() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.COURSES.GET_ALL_ACTIVE_COURSES);
      return response.data;
    } catch (error) {
      console.error('Error fetching groups dropdown:', error);
      throw error;
    }
  }

  async getCertificationDropdown() {
    try {
      const response = await apiClient.get('certification-exams/certifications');
      return response.data;
    } catch (error) {
      console.error('Error fetching certification dropdown:', error);
      throw error;
    }
  }

  async getGroupRoles(groupKey: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUPS.GET_ROLES(groupKey));
      return response.data;
    } catch (error) {
      console.error('Error fetching group roles:', error);
      throw error;
    }
  }
  
  async createGroup(groupData: any) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.GROUPS.CREATE, groupData);
      return response.data;
    } catch (error) {
      console.error('Error creating group:', error);
      throw error;
    }
  }
  
  async updateGroup(key: string, groupData: any) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.GROUPS.UPDATE(key), groupData);
      return response.data;
    } catch (error) {
      console.error('Error updating group:', error);
      throw error;
    }
  }

  async deleteGroup(key: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.GROUPS.DELETE(key));
      return response.data;
    } catch (error) {
      console.error('Error deleting group:', error);
      throw error;
    }
  }
  
  async getRoles(params: any = {}) {
    try {
      // Build request body with all parameters
      const body: any = {};

      if (params.skip !== undefined) body.skip = params.skip;
      if (params.limit !== undefined) body.limit = params.limit || 10;
      if (params.role_name) body.role_name = params.role_name;
      if (params.role_key) body.role_key = params.role_key;

      const response = await apiClient.post(API_ENDPOINTS.ROLES.LIST, body);
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  async getRoleByKey(key: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ROLES.GET_BY_KEY(key));
      return response.data;
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  }
  
  async createRole(roleData: any) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ROLES.CREATE, roleData);
      return response.data;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }
  
  async updateRole(key: string, roleData: any) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.ROLES.UPDATE(key), roleData);
      return response.data;
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  async deleteRole(key: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.ROLES.DELETE(key));
      return response.data;
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }
  
  // Group Role Map methods
  async getGroupRoleMap(groupKey: string) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.GROUP_ROLE_MAP.GET_BY_KEY(groupKey));
      return response.data;
    } catch (error) {
      console.error('Error fetching group role map:', error);
      throw error;
    }
  }

  async createGroupRoleMap(data: any) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.GROUP_ROLE_MAP.CREATE, data);
      return response.data;
    } catch (error) {
      console.error('Error creating group role map:', error);
      throw error;
    }
  }

  async updateGroupRoleMap(groupKey: string, data: any) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.GROUP_ROLE_MAP.UPDATE(groupKey), data);
      return response.data;
    } catch (error) {
      console.error('Error updating group role map:', error);
      throw error;
    }
  }

  async deleteGroupRoleMap(groupKey: string) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.GROUP_ROLE_MAP.DELETE(groupKey));
      return response.data;
    } catch (error) {
      console.error('Error deleting group role map:', error);
      throw error;
    }
  }

  // New API methods for User Course Progress feature
  async getUserDetails(userId: string): Promise<UserDetailsResponse> {
    try {
      const response = await apiClient.get<{
        status: string;
        data: UserDetailsResponse;
        timestamp: string;
        description: string;
      }>(API_ENDPOINTS.USERS.GET_DETAILS(userId));

      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error('Failed to fetch user details');
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }

  async getUserActivityLog(
    userId: string, 
    courseId: string, 
    params?: ActivityLogParams
  ): Promise<ActivityLogResponse> {
    try {
      const response = await apiClient.get<{
        status: string;
        data: ActivityLogResponse;
        timestamp: string;
        description: string;
      }>(API_ENDPOINTS.USERS.GET_ACTIVITY_LOG(userId, courseId), { params });

      if (response.data.status === 'success') {
        return response.data.data;
      }
      throw new Error('Failed to fetch user activity log');
    } catch (error) {
      console.error('Error fetching user activity log:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
